/* eslint-disable no-console */
import React, { RefObject, useMemo, memo, useCallback, useEffect, useRef, useState } from 'react';
import { isEqual } from 'radash';
import TimelineEffect from './TimelineEffect';
import { useAiElementUpdates } from '../../ClipCustomizer/pocAIEdits/hooks/useAiElementUpdates';
import { Effect } from '@/App/remotion/MainPlayer/types/Animations/Effects';
import { useDragElement } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/hooks/useDragElement';
import {
  ElementResizeSideEnum,
  MINIMUM_TRACK_TIME_DIFFERENCE
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelineTrackElementLanes/constants';
import type { ElementResizeSide } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelineTrackElementLanes/types';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';

interface Props {
  effects: Effect[];
  duration: number; // seconds
  disabled?: boolean; // Prevent tooltips when dragging
  newTimelineContainerRef?: RefObject<HTMLDivElement>;
  speakerTimelineContainerRef?: RefObject<HTMLDivElement>;
  startOffsetSeconds?: number;
}

const TimelineEffectsLane: React.FC<Props> = ({
  effects,
  duration,
  disabled = false,
  newTimelineContainerRef,
  speakerTimelineContainerRef,
  startOffsetSeconds = 0
}) => {
  const { fps, clipId } = useClipsContext();
  const { handleEffectMove, handleEffectResize } = useAiElementUpdates();

  const [draggingEffectId, setDraggingEffectId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<number>(0);

  const dragSideRef = useRef<ElementResizeSide>(ElementResizeSideEnum.CENTER);
  const initialPlayableStartRef = useRef<number | null>(null);
  const initialDurationRef = useRef<number | null>(null);
  const workingEffectsRef = useRef<Effect[] | null>(null);
  const prevEffectsRef = useRef<Effect[] | null>(null);

  const [displayEffects, setDisplayEffects] = useState<Effect[]>(effects);

  // Keep displayEffects in sync when not dragging - prevent updates during drag
  useEffect(() => {
    if (draggingEffectId) {
      return; // Don't sync during drag operations
    }

    if (isEqual(prevEffectsRef.current, effects)) {
      return; // No changes to sync
    }

    prevEffectsRef.current = effects;
    setDisplayEffects(effects);
  }, [effects, draggingEffectId]);

  // Live updates during drag based on dragOffset (seconds)
  useEffect(() => {
    if (!draggingEffectId || dragOffset === 0) {
      return;
    }

    setDisplayEffects(prevDisplayEffects => {
      const minDur = MINIMUM_TRACK_TIME_DIFFERENCE; // seconds
      const baseEffects = prevDisplayEffects.length ? prevDisplayEffects : effects;
      const updated = baseEffects.map(eff => {
        if (eff.id !== draggingEffectId) {
          return eff;
        }

        // Initialize refs on first encounter
        if (initialPlayableStartRef.current === null || initialDurationRef.current === null) {
          initialPlayableStartRef.current = eff.start - startOffsetSeconds;
          initialDurationRef.current = eff.duration;
        }

        const initialPlayableStart = initialPlayableStartRef.current;
        const initialDuration = initialDurationRef.current;
        const initialPlayableEnd = initialPlayableStart + initialDuration;

        let newPlayableStart = initialPlayableStart;
        let newDuration = initialDuration;

        if (dragSideRef.current === ElementResizeSideEnum.CENTER) {
          newPlayableStart = Math.max(0, Math.min(initialPlayableStart + dragOffset, duration - initialDuration));
        } else if (dragSideRef.current === ElementResizeSideEnum.START) {
          const tentativeStart = initialPlayableStart + dragOffset;
          // Clamp start not beyond end - minDur and not before 0
          newPlayableStart = Math.max(0, Math.min(tentativeStart, initialPlayableEnd - minDur));
          newDuration = Math.max(minDur, initialPlayableEnd - newPlayableStart);
        } else if (dragSideRef.current === ElementResizeSideEnum.END) {
          const tentativeDuration = initialDuration + dragOffset;
          const maxDuration = duration - initialPlayableStart;
          newDuration = Math.max(minDur, Math.min(tentativeDuration, maxDuration));
        }

        // Produce a working copy with updated start/duration
        const newStartAbsolute = newPlayableStart + startOffsetSeconds;

        // Scale animation durations proportionally during live resize
        const scaleAnimationDuration = (value: number | undefined, ratio: number) => {
          if (!value || value < 0.01) {
            return 0;
          }
          return parseFloat((value * ratio).toFixed(2));
        };

        let scaledAnimationInDuration = eff.animationInDuration;
        let scaledAnimationOutDuration = eff.animationOutDuration;

        if (initialDuration !== newDuration && initialDuration > 0) {
          const durationRatio = newDuration / initialDuration;
          scaledAnimationInDuration = scaleAnimationDuration(eff.animationInDuration, durationRatio);
          scaledAnimationOutDuration = scaleAnimationDuration(eff.animationOutDuration, durationRatio);
        }

        const updatedEffect: Effect = {
          ...eff,
          start: newStartAbsolute,
          duration: newDuration,
          ...(scaledAnimationInDuration !== undefined && { animationInDuration: scaledAnimationInDuration }),
          ...(scaledAnimationOutDuration !== undefined && { animationOutDuration: scaledAnimationOutDuration })
        } as Effect;
        return updatedEffect;
      });

      workingEffectsRef.current = updated;
      return updated;
    });
  }, [draggingEffectId, dragOffset, duration, effects, startOffsetSeconds]);

  const onEffectDragEnd = useCallback(
    (effectId?: string) => {
      // Use the passed effectId or fallback to draggingEffectId
      const targetEffectId = effectId || draggingEffectId;

      if (!workingEffectsRef.current) {
        return;
      }

      // Find the final effect with updates
      const finalEffects = workingEffectsRef.current;
      const updatedEffect = finalEffects.find(e => e.id === targetEffectId);

      if (updatedEffect) {
        // Persist move vs resize
        if (dragSideRef.current === ElementResizeSideEnum.CENTER) {
          handleEffectMove(clipId, updatedEffect.id, updatedEffect.start);
        } else {
          handleEffectResize(clipId, updatedEffect.id, updatedEffect.start, updatedEffect.duration);
        }
      }

      // Reset refs for next drag
      initialPlayableStartRef.current = null;
      initialDurationRef.current = null;
      workingEffectsRef.current = null;
    },
    [clipId, draggingEffectId, handleEffectMove, handleEffectResize]
  );

  const { onMouseDown } = useDragElement({
    setDraggingElement: id => setDraggingEffectId(id),
    setDraggingElementOffset: setDragOffset,
    onDragEnd: (_, dragId) => {
      // Call our drag end handler with the drag ID since draggingEffectId might be null
      onEffectDragEnd(dragId);

      // Reset state
      setDraggingEffectId(null);
      setDragOffset(0);
    },
    validDropZoneRef: newTimelineContainerRef as RefObject<HTMLDivElement>,
    droppableContainerRef: speakerTimelineContainerRef as RefObject<HTMLDivElement>,
    fps
  });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, effectId: string, side: ElementResizeSide) => {
      e.stopPropagation();
      dragSideRef.current = side;
      onMouseDown(e, effectId);
    },
    [onMouseDown]
  );

  const handleEffectClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  const showEffectsRow = useMemo(() => displayEffects.length > 0, [displayEffects.length]);

  if (!showEffectsRow) {
    return null;
  }

  return (
    <div className="flex w-full flex-col overflow-hidden" style={{ pointerEvents: 'auto' }}>
      <div className="relative flex h-[32px] min-h-6 w-full items-center">
        {displayEffects.map(effect => (
          <TimelineEffect
            key={effect.id}
            effect={effect}
            disabled={disabled}
            newTimelineContainerRef={newTimelineContainerRef}
            speakerTimelineContainerRef={speakerTimelineContainerRef}
            startOffsetSeconds={startOffsetSeconds}
            onMouseDown={handleMouseDown}
            side={effect.id === draggingEffectId ? dragSideRef.current : ElementResizeSideEnum.CENTER}
            isDragging={effect.id === draggingEffectId}
            isSelected={false}
            onEffectClick={(e, _id) => handleEffectClick(e)}
          />
        ))}
      </div>
    </div>
  );
};

export default memo(TimelineEffectsLane);
